import { useCallback, useEffect, useMemo, useState } from 'react'
import { CreditCard, Plus, Tag, Trash2, Wallet, X, Zap } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { openWhatsAppBooking } from '@/lib/calendar/appointmentWhatsApp'
import {
  buildPayPayload,
  canOpenPaymentModal,
  computeAmountDue,
  computeChangeDue,
  computePaymentRemaining,
  createPaymentRow,
  getCashPaymentAmount,
  getReferenceLabel,
  MAX_SPLIT_PAYMENTS,
  requiresPaymentReference,
  resolveDefaultTipStylistId,
  roundMoney,
  sumPaymentRows,
  validatePaymentRows,
  validateTipSelection,
} from '@/components/pos/posPayment'
import { getParentSaleLines, toPosTotals } from '@/components/pos/posCart'
import { PosTotalsBreakdown } from '@/components/pos/PosTotalsBreakdown'
import { cn } from '@/lib/utils'
import { filterCheckoutPaymentMethods, SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'

const METHOD_ICONS = {
  cash: Wallet,
  card: CreditCard,
  apple_pay: CreditCard,
  transfer: CreditCard,
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {import('@/types/api').PaymentMethod[]} props.paymentMethods
 * @param {boolean} props.loadingMethods
 * @param {boolean} props.submitting
 * @param {(payload: ReturnType<typeof buildPayPayload>) => Promise<{ changeDue?: number | null, receipt?: import('@/types/api').PosReceipt | null, taxInvoice?: import('@/types/api').PosTaxInvoice | null, serviceSlips?: import('@/types/api').PosServiceSlips | null } | null>} props.onSubmit
 * @param {import('@/types/api').Stylist[]} [props.stylists]
 * @param {() => void} [props.onPrintReceipt]
 * @param {() => void} [props.onPrintTaxInvoice]
 * @param {() => void | Promise<void>} [props.onPrintServiceSlips]
 * @param {string} [props.apiError]
 * @param {boolean} [props.gatewayEnabled]
 * @param {boolean} [props.gatewayTestMode]
 * @param {(payload: { amount: number, card_last4?: string }) => Promise<{ gateway_reference?: string, amount?: number } | null>} [props.onChargeGateway]
 * @param {(payload: { code: string }) => Promise<void>} [props.onApplyPromoCode]
 * @param {() => Promise<void>} [props.onRemovePromoCode]
 */
export function PosPaymentModal({
  open,
  onClose,
  sale,
  paymentMethods,
  loadingMethods,
  submitting,
  onSubmit,
  onPrintReceipt,
  onPrintTaxInvoice,
  onPrintServiceSlips,
  apiError = '',
  stylists = [],
  gatewayEnabled = false,
  gatewayTestMode = true,
  onChargeGateway,
  onApplyPromoCode,
  onRemovePromoCode,
}) {
  const [tipAmount, setTipAmount] = useState(0)
  const [tipType, setTipType] = useState('direct')
  const [tipStylistId, setTipStylistId] = useState('')
  const [cashTendered, setCashTendered] = useState('')
  const [rows, setRows] = useState([])
  const [errors, setErrors] = useState([])
  const [successChange, setSuccessChange] = useState(null)
  const [receiptReady, setReceiptReady] = useState(false)
  const [whatsappSharing, setWhatsappSharing] = useState(false)
  const [whatsappError, setWhatsappError] = useState('')
  const [taxInvoiceReady, setTaxInvoiceReady] = useState(false)
  const [serviceSlipsReady, setServiceSlipsReady] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [promoError, setPromoError] = useState('')
  const [promoApplying, setPromoApplying] = useState(false)
  const [gatewayError, setGatewayError] = useState('')
  const [gatewayCharging, setGatewayCharging] = useState(false)
  const [gatewayReference, setGatewayReference] = useState('')

  const depositCredit = toNumber(sale?.deposit_credit)
  const hasPromo = Boolean(sale?.promo_code_id)
  const visiblePaymentMethods = useMemo(
    () => filterCheckoutPaymentMethods(paymentMethods),
    [paymentMethods],
  )
  const simplified = SALON_DESK_CHECKOUT.lite

  const amountDue = useMemo(
    () => computeAmountDue(sale, tipAmount),
    [sale, tipAmount],
  )

  const lineCount = getParentSaleLines(sale).length

  useEffect(() => {
    if (!open) return

    setTipAmount(0)
    setTipType('direct')
    setTipStylistId(resolveDefaultTipStylistId(sale) ?? '')
    setCashTendered('')
    setErrors([])
    setSuccessChange(null)
    setReceiptReady(false)
    setWhatsappSharing(false)
    setWhatsappError('')
    setTaxInvoiceReady(false)
    setServiceSlipsReady(false)
    setPromoCode('')
    setPromoError('')
    setGatewayError('')
    setGatewayReference('')
    setRows(visiblePaymentMethods.length ? [createPaymentRow(visiblePaymentMethods)] : [])
  }, [open, visiblePaymentMethods, sale?.id])

  useEffect(() => {
    if (!open || rows.length !== 1) return
    setRows((current) => current.map((row, index) => (
      index === 0 && !row.amount
        ? { ...row, amount: amountDue.toFixed(2) }
        : row
    )))
  }, [open, amountDue, rows.length])

  const remaining = computePaymentRemaining(amountDue, rows)
  const changePreview = computeChangeDue(toNumber(cashTendered), rows, paymentMethods)
  const hasCashPayment = getCashPaymentAmount(rows, paymentMethods) > 0

  const updateRow = useCallback((key, patch) => {
    setRows((current) => current.map((row) => (
      row.key === key ? { ...row, ...patch } : row
    )))
  }, [])

  const addRow = useCallback(() => {
    if (simplified && !SALON_DESK_CHECKOUT.allowSplitPayment) return
    setRows((current) => {
      if (current.length >= MAX_SPLIT_PAYMENTS) return current
      const usedIds = new Set(current.map((row) => row.payment_method_id))
      const nextMethod = visiblePaymentMethods.find((method) => !usedIds.has(method.id))
      const nextRow = createPaymentRow(visiblePaymentMethods, nextMethod?.id ?? '')
      const fillAmount = computePaymentRemaining(amountDue, current)
      if (fillAmount > 0) {
        nextRow.amount = fillAmount.toFixed(2)
      }
      return [...current, nextRow]
    })
  }, [amountDue, simplified, visiblePaymentMethods])

  const removeRow = useCallback((key) => {
    setRows((current) => current.filter((row) => row.key !== key))
  }, [])

  const handleApplyPromo = async () => {
    if (!onApplyPromoCode || !promoCode.trim()) return
    setPromoApplying(true)
    setPromoError('')
    try {
      await onApplyPromoCode({ code: promoCode.trim() })
      setPromoCode('')
    } catch (err) {
      setPromoError(err instanceof Error ? err.message : 'Unable to apply promo code.')
    } finally {
      setPromoApplying(false)
    }
  }

  const handleRemovePromo = async () => {
    if (!onRemovePromoCode) return
    setPromoApplying(true)
    setPromoError('')
    try {
      await onRemovePromoCode()
    } catch (err) {
      setPromoError(err instanceof Error ? err.message : 'Unable to remove promo code.')
    } finally {
      setPromoApplying(false)
    }
  }

  const handleGatewayCapture = async () => {
    if (!onChargeGateway || remaining <= 0) return
    setGatewayCharging(true)
    setGatewayError('')
    try {
      const captureAmount = Math.max(0.01, remaining)
      const result = await onChargeGateway({ amount: captureAmount })
      if (!result?.gateway_reference) return

      setGatewayReference(result.gateway_reference)
      const cardMethod = paymentMethods.find((method) => method.code === 'card') ?? paymentMethods[0]
      setRows((current) => {
        const usedIds = new Set(current.map((row) => row.payment_method_id))
        const emptyRow = current.find((row) => !row.amount || toNumber(row.amount) <= 0)
        const targetKey = emptyRow?.key ?? current[0]?.key

        if (targetKey) {
          return current.map((row) => (
            row.key === targetKey
              ? {
                  ...row,
                  payment_method_id: cardMethod?.id ?? row.payment_method_id,
                  amount: captureAmount.toFixed(2),
                  reference: result.gateway_reference,
                }
              : row
          ))
        }

        if (current.length >= MAX_SPLIT_PAYMENTS) return current

        return [
          ...current,
          {
            ...createPaymentRow(paymentMethods, cardMethod?.id ?? ''),
            amount: captureAmount.toFixed(2),
            reference: result.gateway_reference,
          },
        ]
      })
    } catch (err) {
      setGatewayError(err instanceof Error ? err.message : 'Gateway capture failed.')
    } finally {
      setGatewayCharging(false)
    }
  }

  const handleSubmit = async () => {
    const validationErrors = [
      ...validateTipSelection(tipAmount, tipType, tipStylistId === '' ? null : Number(tipStylistId)),
      ...validatePaymentRows(rows, paymentMethods, amountDue),
    ]
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors([])
    const result = await onSubmit(buildPayPayload(
      rows,
      tipAmount,
      toNumber(cashTendered),
      paymentMethods,
      {
        tipType,
        tipStylistId: tipStylistId === '' ? null : Number(tipStylistId),
      },
    ))
    if (result) {
      setSuccessChange(result.changeDue ?? changePreview)
      setReceiptReady(Boolean(result.receipt))
      setTaxInvoiceReady(Boolean(result.taxInvoice))
      setServiceSlipsReady(Boolean(result.serviceSlips))
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-payment-title"
        className="card-featured flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden shadow-luxury-lg"
      >
        <div className="flex items-center justify-between border-b border-primary/10 px-4 py-3">
          <div>
            <p id="pos-payment-title" className="text-sm font-semibold">Take payment</p>
            <p className="text-[11px] text-muted-foreground">
              {sale?.sale_number} · {lineCount} {lineCount === 1 ? 'service' : 'services'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted/80"
            aria-label="Close payment"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {successChange !== null ? (
            <div className="rounded-2xl bg-emerald-50 p-4 text-center">
              <p className="text-sm font-semibold text-emerald-800">Payment complete</p>
              <p className="mt-2 text-3xl font-bold text-emerald-900">
                Change: AED {roundMoney(successChange).toFixed(2)}
              </p>
              <p className="mt-2 text-[11px] text-emerald-700">
                {receiptReady
                  ? 'Receipt sent to printer. Start the next ticket when ready.'
                  : 'Payment saved. Receipt could not be loaded.'}
              </p>
              {receiptReady && onPrintReceipt ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={onPrintReceipt}
                >
                  Print receipt again
                </Button>
              ) : null}
              {taxInvoiceReady && onPrintTaxInvoice && SALON_DESK_CHECKOUT.showTaxInvoice ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={onPrintTaxInvoice}
                >
                  Print tax invoice (A4)
                </Button>
              ) : null}
              {serviceSlipsReady && onPrintServiceSlips && SALON_DESK_CHECKOUT.showServiceSlips ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={onPrintServiceSlips}
                >
                  Print stylist slips
                </Button>
              ) : null}
              {SALON_DESK_CHECKOUT.showWhatsAppReceiptShare && sale?.id ? (
                <div className="mt-3 space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={whatsappSharing}
                    onClick={async () => {
                      setWhatsappSharing(true)
                      setWhatsappError('')
                      try {
                        const { data } = await api.get(`/sales/${sale.id}/receipt-share`)
                        const pdfUrl = data?.data?.pdf_url ?? data?.data?.view_url
                        const message = [
                          'Thank you for visiting us.',
                          pdfUrl ? `Your receipt: ${pdfUrl}` : 'Your payment was received.',
                        ].join('\n')
                        openWhatsAppBooking({
                          phone: sale.customer?.phone,
                          message,
                        })
                      } catch (err) {
                        setWhatsappError(apiErrorMessage(err, 'Unable to prepare WhatsApp receipt.'))
                      } finally {
                        setWhatsappSharing(false)
                      }
                    }}
                  >
                    {whatsappSharing ? 'Preparing…' : 'Share on WhatsApp'}
                  </Button>
                  {whatsappError ? (
                    <p className="text-[11px] text-destructive">{whatsappError}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div className={cn('grid gap-3 rounded-xl bg-muted/30 p-3', simplified ? 'grid-cols-1' : 'grid-cols-2')}>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Amount due</p>
                  <p className="text-xl font-bold text-primary">AED {amountDue.toFixed(2)}</p>
                  {depositCredit > 0 ? (
                    <p className="mt-1 text-[10px] text-emerald-700">
                      Includes AED {depositCredit.toFixed(2)} online deposit credit
                    </p>
                  ) : null}
                  {SALON_DESK_CHECKOUT.showTotalsBreakdown ? (
                    <PosTotalsBreakdown
                      totals={toPosTotals(sale)}
                      variant="compact"
                      className="mt-2 border-t border-border/50 pt-2"
                    />
                  ) : null}
                </div>
                {!SALON_DESK_CHECKOUT.showTips ? null : (
                <label>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Tip (AED)</span>
                  <input
                    type="number"
                    min="0"
                    step="5"
                    value={tipAmount || ''}
                    disabled={submitting || loadingMethods}
                    onChange={(event) => setTipAmount(Math.max(0, toNumber(event.target.value)))}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                  />
                </label>
                )}
              </div>

              {SALON_DESK_CHECKOUT.showTips && tipAmount > 0 ? (
                <div className="space-y-3 rounded-xl border border-border/60 bg-card p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Tip allocation
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="tip-type"
                        value="direct"
                        checked={tipType === 'direct'}
                        disabled={submitting}
                        onChange={() => setTipType('direct')}
                      />
                      Direct to stylist
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="tip-type"
                        value="pool"
                        checked={tipType === 'pool'}
                        disabled={submitting}
                        onChange={() => setTipType('pool')}
                      />
                      Team pool
                    </label>
                  </div>
                  {tipType === 'direct' ? (
                    <label className="block">
                      <span className="mb-1 block text-xs text-muted-foreground">Stylist</span>
                      <select
                        value={tipStylistId}
                        disabled={submitting}
                        onChange={(event) => setTipStylistId(event.target.value)}
                        className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm"
                      >
                        <option value="">Select stylist</option>
                        {stylists.map((stylist) => (
                          <option key={stylist.id} value={stylist.id}>
                            {stylist.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Pool tips are shared across the team and paid out from Masters.
                    </p>
                  )}
                </div>
              ) : null}

              {onApplyPromoCode && SALON_DESK_CHECKOUT.showPromoCodes ? (
                <div className="rounded-xl border border-border/60 bg-card p-3">
                  <div className="flex items-center gap-2">
                    <Tag className="h-3.5 w-3.5 text-primary" aria-hidden />
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Promo code
                    </p>
                  </div>
                  {hasPromo ? (
                    <div className="mt-2 flex items-center justify-between gap-2 text-sm">
                      <span className="text-emerald-700">Promo applied to ticket</span>
                      <button
                        type="button"
                        disabled={submitting || promoApplying}
                        onClick={handleRemovePromo}
                        className="text-xs font-semibold text-destructive"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        disabled={submitting || promoApplying}
                        onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                        placeholder="SUMMER20"
                        className="min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-xs uppercase"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={submitting || promoApplying || !promoCode.trim()}
                        onClick={handleApplyPromo}
                      >
                        {promoApplying ? '…' : 'Apply'}
                      </Button>
                    </div>
                  )}
                  {promoError ? <p className="mt-1 text-[11px] text-destructive">{promoError}</p> : null}
                </div>
              ) : null}

              {gatewayEnabled && onChargeGateway && SALON_DESK_CHECKOUT.showGateway ? (
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Gateway capture
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {gatewayTestMode ? 'Test mode — stub capture' : 'Manual card capture'}
                      </p>
                      {gatewayReference ? (
                        <p className="mt-1 font-mono text-[10px] text-foreground">{gatewayReference}</p>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={submitting || gatewayCharging || remaining <= 0}
                      onClick={handleGatewayCapture}
                    >
                      <Zap className="mr-1 h-3.5 w-3.5" />
                      {gatewayCharging ? 'Capturing…' : 'Capture'}
                    </Button>
                  </div>
                  {gatewayError ? <p className="mt-2 text-[11px] text-destructive">{gatewayError}</p> : null}
                </div>
              ) : null}

              {loadingMethods ? (
                <p className="text-sm text-muted-foreground">Loading payment methods…</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {simplified ? 'Payment' : 'Split payment'}
                    </p>
                    {!simplified || SALON_DESK_CHECKOUT.allowSplitPayment ? (
                    <button
                      type="button"
                      disabled={submitting || rows.length >= MAX_SPLIT_PAYMENTS}
                      onClick={addRow}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary disabled:opacity-50"
                    >
                      <Plus className="h-3 w-3" />
                      Add method
                    </button>
                    ) : null}
                  </div>

                  {rows.map((row, index) => {
                    const method = visiblePaymentMethods.find((item) => item.id === row.payment_method_id)
                    const Icon = METHOD_ICONS[method?.code ?? ''] ?? CreditCard

                    return (
                      <div key={row.key} className="rounded-xl border border-border/60 bg-card p-3">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                          <select
                            value={row.payment_method_id}
                            disabled={submitting}
                            onChange={(event) => updateRow(row.key, {
                              payment_method_id: Number(event.target.value),
                              reference: '',
                            })}
                            className="min-w-0 flex-1 rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
                          >
                            {visiblePaymentMethods.map((item) => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            disabled={submitting}
                            value={row.amount}
                            onChange={(event) => updateRow(row.key, { amount: event.target.value })}
                            placeholder="0.00"
                            className="w-24 rounded-lg border border-input bg-background px-2 py-1.5 text-right text-xs"
                          />
                          {rows.length > 1 ? (
                            <button
                              type="button"
                              disabled={submitting}
                              onClick={() => removeRow(row.key)}
                              className="text-destructive hover:bg-destructive/5 rounded-lg p-1.5"
                              aria-label={`Remove payment ${index + 1}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                        </div>

                        {method && requiresPaymentReference(method) ? (
                          <input
                            type="text"
                            disabled={submitting}
                            value={row.reference}
                            onChange={(event) => updateRow(row.key, { reference: event.target.value })}
                            placeholder={getReferenceLabel(method)}
                            className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 text-xs"
                          />
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Allocated</span>
                <span className={cn(
                  'font-semibold',
                  Math.abs(remaining) <= 0.01 ? 'text-emerald-700' : 'text-foreground',
                )}
                >
                  AED {sumPaymentRows(rows).toFixed(2)}
                  {Math.abs(remaining) > 0.01 ? ` · Remaining AED ${remaining.toFixed(2)}` : ''}
                </span>
              </div>

              {hasCashPayment ? (
                <label className="block rounded-xl border border-gold/30 bg-gold/10 p-3">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Cash tendered (for change)
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    disabled={submitting}
                    value={cashTendered}
                    onChange={(event) => setCashTendered(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-[11px] font-medium text-foreground">
                    Change due: AED {changePreview.toFixed(2)}
                  </p>
                </label>
              ) : null}

              {errors.length > 0 || apiError ? (
                <ul className="space-y-1 rounded-xl bg-destructive/5 p-3 text-[11px] text-destructive">
                  {apiError ? <li>{apiError}</li> : null}
                  {errors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </div>

        <div className="flex gap-2 border-t border-border/50 bg-muted/20 p-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={submitting}
            onClick={onClose}
          >
            {successChange !== null ? 'New ticket' : 'Cancel'}
          </Button>
          {successChange === null ? (
            <Button
              type="button"
              className="flex-1"
              disabled={submitting || loadingMethods || !canOpenPaymentModal(sale)}
              onClick={handleSubmit}
            >
              {submitting ? 'Processing…' : 'Complete payment'}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/**
 * @param {number | string | null | undefined} value
 */
function toNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}
